import {
  CalendarFilled,
  ClockCircleFilled,
  ClockCircleOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import { BookOpenIcon } from '@heroicons/react/outline';
import { ChevronRightIcon, GlobeIcon, PlusIcon } from '@heroicons/react/solid';
import { Button, Collapse } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import parsePhoneNumberFromString, { ParseError } from 'libphonenumber-js';
import moment, { Moment } from 'moment';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useRecoilState, useRecoilValue } from 'recoil';

import { useSessionTracking } from '@/hooks/useSessionTracking';

import LiveAuthorCard from '@/components/Live/LiveCard/LiveAuthorCard';
import PhonePasswordLiveModal from '@/components/Live/PhonePasswordLiveModal';
import { formateErrorObject } from '@/components/shared/formateErrorObject';
import { setUserId } from '@/components/shared/getToken';
import { handleWorkShopSignup } from '@/components/shared/Gtags';

import appwriteService from '@/config/appwrite';
import { getUserProfile } from '@/services/user';
import { EventV2 } from '@/utils/Events/events';

import type { FAQItem } from '../../refer-earn';
import BrainIcon from '../../../../assets/Icons/BrainIcon';
import ChartIcon from '../../../../assets/Icons/ChartIcon';
import LocationIcon from '../../../../assets/Icons/LocationIcon';
import { tokenUpdate } from '../../../atoms/authModalControl';
import { user } from '../../../atoms/user';
import RecommendedBooksSection from '../../../components/BookLandingPageComp/RecommendedBooksSection';
import { customExpandIcon } from '../../../components/CartPage';
import { Spinner } from '../../../components/shared/basic/button';
import { ShouldRender } from '../../../components/shared/basic/ShouldRender';
import Countdown from '../../../components/shared/Countdown';
import CustomLoader from '../../../components/shared/Loading';
import { notify } from '../../../components/shared/notify';
import ForgotPasswordModal from '../../../components/V2/ForgotPasswordModal';
import LoginModal from '../../../components/V2/LoginModal';
import SignUpModal from '../../../components/V2/SignUpModal';
import { EventDetails, logEvent } from '../../../hooks/useClickEventTracking';
import {
  getLiveDetails,
  liveRegistrationAlreadyRegistered,
  updateUserPhoneNumber,
} from '../../../services/Live';
import { User } from '../../../utils/interfaces/User';

const { Panel } = Collapse;
const faqItems: FAQItem[] = [
  {
    question: 'What is ZebraLearn Live?',
    answer:
      'ZebraLearn Live brings expert-led sessions straight to you, making complex topics easier to understand. Join live classes on business, finance, marketing, and more – all in real time. Interact with industry pros, ask questions, and get practical insights instantly. Perfect for students, professionals, and curious learners.',
  },
  {
    question: 'Join sessions led by industry experts.',
    answer:
      'Our industry experts bring years of experience and knowledge to each session, providing you with valuable insights and practical skills you can apply immediately.',
  },
  {
    question: 'Network with fellow learners and professionals.',
    answer:
      'Connect with like-minded individuals in your field, share experiences, and build valuable professional relationships that can help advance your career.',
  },
  {
    question: 'Access a library of recorded sessions.',
    answer:
      "Can't make it to a live session? No problem. All our workshops are recorded and available in our comprehensive library for you to access at your convenience.",
  },
  {
    question: 'Receive personalized feedback on projects.',
    answer:
      'Submit your work and receive detailed, constructive feedback from our experts to help you improve and refine your skills.',
  },
];

interface CourseBenefit {
  icon: JSX.Element;
  title: string;
}

export interface FormDataPassword {
  password: string;
}

export interface PhoneData {
  phone: string;
  phoneCountryCode: string;
  phonePrefix: string;
}

interface ErrorState {
  password: string;
  phone: string;
}

export interface PasswordModalState {
  formData: FormDataPassword;
  phoneData: PhoneData;
  errors: ErrorState;
  modalVisible: boolean;
  loading: boolean;
}

interface Module {
  id: number;
  title: string;
  content: string;
}

interface EventDetailsLive {
  date: string;
  day: string;
  time: string;
  duration: string;
  language: string;
  start_time: string;
  end_time?: string;
  subtitles: string;
  qa: string;
  chat: string;
  registrations: number;
}

export interface Author {
  name: string;
  verified: boolean;
  title: string;
  learners: number;
  bio: string;
  longBio: string;
  is_followed: boolean;
  user_name: string;
  authorId: string;
  author_zlUid: string;
  profileImage: string;
  designation?: string;
  company?: string;
}

interface ILiveDetailsData {
  title: string;
  tag: string;
  thumbnail: string;
  description: string;
  eventDetails: EventDetailsLive;
  benefits: CourseBenefit[];
  modules: Module[];
  author: Author;
  isRegister: boolean;
  category?: string;
  iframe_type?: string;
  status: string;
  iframe?: string;
}

interface Props {
  metaData: {
    title: string;
    description: string;
    author: string;
    keywords: string;
    image: string;
    openGraph: {
      title: string;
      description: string;
      image: string;
    };
    categories?: [
      {
        _id: string;
        name: string;
      },
    ];
    slug: string;
    price: number;
    reviews: any[];
    ratingValue: number;
    reviewCount: number;
  } | null;
}

export const formatTime = (time: Moment): string =>
  time.minutes() === 0 ? time.format('h A') : time.format('h:mm A');

const LiveLandingPage: React.FC<Props> = ({ metaData }) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const { ref, inView } = useInView({
    threshold: 0.1,
  });

  const [showFixedButton, setShowFixedButton] = useState(false);
  const [passwordState, setPasswordState] = useState<PasswordModalState>({
    formData: {
      password: '',
    },
    phoneData: {
      phone: '',
      phoneCountryCode: '',
      phonePrefix: '',
    },
    errors: {
      password: '',
      phone: '',
    },
    modalVisible: false,
    loading: false,
  });
  const [liveDetails, setLiveDetails] = useState<ILiveDetailsData>();
  const [visible, setVisible] = useState<{
    login: boolean;
    signUp: boolean;
    forgotPassword: boolean;
    bookId: string;
  }>({
    login: false,
    signUp: false,
    forgotPassword: false,
    bookId: '',
  });
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(true);
  const [alreadyRegisterLoading, setAlreadyRegisterLoading] = useState(false);
  const isTokenChanged = useRecoilValue(tokenUpdate);

  const [userData, setUserData] = useRecoilState<User | undefined>(user);
  const router = useRouter();
  const { slug, workshop_id } = router.query;

  const eventDate = moment(liveDetails?.eventDetails?.date);

  const day = eventDate.date();
  const ordinal = eventDate.format('Do').replace(/\d+/g, '');
  const monthYear = eventDate.format('MMMM, YYYY');
  const weekday = eventDate.format('dddd');
  const screens = useBreakpoint();
  const toggleAccordion = useCallback(
    (index: number) => {
      setActiveIndex(activeIndex === index ? -1 : index);
    },
    [activeIndex],
  );

  const setFormDataPassword = (
    updater: FormDataPassword | ((prev: FormDataPassword) => FormDataPassword),
  ) => {
    setPasswordState((prev) => ({
      ...prev,
      formData:
        typeof updater === 'function' ? updater(prev.formData) : updater,
    }));
  };

  const handlePhoneInput = ({
    phone,
    phoneCountryCode,
    phonePrefix,
  }: {
    phone: string;
    phoneCountryCode: string;
    phonePrefix: string;
  }) => {
    const cleanedValue = phone.replace(/\D/g, '');

    // Update phoneData
    setPasswordState((prev) => ({
      ...prev,
      phoneData: {
        phone: cleanedValue,
        phoneCountryCode,
        phonePrefix,
      },
    }));

    try {
      if (!cleanedValue.trim()) {
        setPasswordState((prev) => ({
          ...prev,
          errors: {
            ...prev.errors,
            phone: 'Phone number is required',
          },
        }));
        return;
      }

      const parsedPhoneNumber = parsePhoneNumberFromString(
        `+${phonePrefix}${cleanedValue}`,
      );

      if (!parsedPhoneNumber) {
        setPasswordState((prev) => ({
          ...prev,
          errors: {
            ...prev.errors,
            phone: 'Invalid phone number format',
          },
        }));
      } else if (!parsedPhoneNumber.isValid()) {
        setPasswordState((prev) => ({
          ...prev,
          errors: {
            ...prev.errors,
            phone:
              'Invalid phone number. Please check the number and try again.',
          },
        }));
      } else {
        setPasswordState((prev) => ({
          ...prev,
          errors: {
            ...prev.errors,
            phone: '',
          },
        }));
      }
    } catch (error: any) {
      if (error instanceof ParseError) {
        setPasswordState((prev) => ({
          ...prev,
          errors: {
            ...prev.errors,
            phone: error.message,
          },
        }));
      } else {
        setPasswordState((prev) => ({
          ...prev,
          errors: {
            ...prev.errors,
            phone:
              'An error occurred while validating the phone number. Please try again.',
          },
        }));
      }
    }
  };

  const setFormDataPhone: React.Dispatch<React.SetStateAction<PhoneData>> = (
    value,
  ) => {
    setPasswordState((prev) => ({
      ...prev,
      phoneData: typeof value === 'function' ? value(prev.phoneData) : value,
    }));
  };

  useEffect(() => {
    setShowFixedButton(!inView);
  }, [inView]);

  const onCancelCloseModal = () => {
    setVisible((prevState) => ({
      ...prevState,
      signUp: false,
      subHeadingCopy: '',
      headingCopy: '',
    }));
  };

  const onCancelCloseLoginModal = () => {
    setVisible((prevState) => ({
      ...prevState,
      login: false,
      subHeadingCopy: '',
      headingCopy: '',
    }));
  };

  const onCancelCloseForgotPasswordModal = () => {
    setVisible((prevState) => ({
      ...prevState,
      forgotPassword: false,
    }));
  };

  const icons = [
    <BookOpenIcon
      key='book-open'
      className='h-6 w-6 text-[24px] text-[#ff5722]'
    />,
    <ClockCircleOutlined
      key='clock-circle'
      className=' text-[24px] text-[#ff5722]'
    />,
    <LocationIcon key='desktop' />,
    <ChartIcon key='level' />,
    <RocketOutlined
      key='play'
      className='text-[24px] text-[#ff5722] rotate-45'
    />,
    <BrainIcon key='trophy' />,
  ];
  const formatLiveData = (data: any): ILiveDetailsData => {
    const parsedExtra =
      Array.isArray(data?.extra) && data.extra.length > 0
        ? data.extra.map((item: string) => {
          try {
            return JSON.parse(item);
          } catch (e) {
            return {};
          }
        })
        : [];

    const modules =
      Array.isArray(data?.modules) && data.modules.length > 0
        ? data.modules.map((m: string, index: number) => {
          try {
            const parsed = JSON.parse(m);
            return {
              id: index + 1,
              title: parsed?.title || '',
              content: parsed?.description || '',
            };
          } catch (e) {
            return {
              id: index + 1,
              title: '',
              content: '',
            };
          }
        })
        : [];

    const start = moment(data?.start_time, 'HH:mm:ss', true);
    const end = moment(data?.end_time, 'HH:mm:ss', true);

    let duration = '';
    if (start.isValid() && end.isValid()) {
      if (end.isBefore(start)) end.add(1, 'day');
      const dur = moment.duration(end.diff(start));
      const hours = Math.floor(dur.asHours());
      const minutes = dur.minutes();
      const hourPart = hours > 0 ? `${hours} hour${hours > 1 ? 's' : ''}` : '';
      const minutePart =
        minutes > 0 ? `${minutes} minute${minutes > 1 ? 's' : ''}` : '';
      duration = [hourPart, minutePart].filter(Boolean).join(' ');
    }

    return {
      title: data?.title || '',
      tag: 'FREE',
      isRegister: data?.is_registered || false,
      thumbnail: data?.thumbnail || '',
      description: data?.description || '',
      status: data?.status,
      category: data?.category || '',
      iframe_type: data?.iframe_type || '',
      iframe: data?.iframe || '',
      eventDetails: {
        date: data?.date || '',
        day: data?.date
          ? new Date(data.date).toLocaleDateString('en-GB', {
            weekday: 'long',
          })
          : '',
        start_time: data?.start_time || '',
        end_time: data?.end_time || '',
        time:
          start.isValid() && end.isValid()
            ? `${formatTime(start)} - ${formatTime(end)}`
            : '',
        duration,
        language: 'English',
        subtitles: 'Subtitles available',
        qa: 'Q & A',
        chat: '1:1 Chat with Instructor',
        registrations: data?.registration_count || 0,
      },
      benefits: parsedExtra.map((item: { value?: string }, index: number) => ({
        icon: icons[index] || '',
        title: item?.value || '',
      })),
      modules,
      author: {
        name: data?.author?.profile_data?.name || data?.author_name || '',
        verified: data?.author?.is_verified || false,
        title: data?.author?.profile_data?.designation || '',
        learners: data?.author?.follower_count || 0,
        bio: data?.author?.profile_data?.bio || '',
        longBio:
          data?.author?.profile_data?.emp_work_experience?.description || '',
        is_followed: data?.author?.is_followed || false,
        profileImage: data?.author?.profile_data?.avatar || '',
        user_name: data?.author?.profile_data?.user_name || '',
        authorId: data?.author?.id || '',
        author_zlUid: data?.author?.zl_uid || '',
        designation:
          data?.author?.profile_data?.emp_work_experience?.designation || '',
        company: data?.author?.profile_data?.emp_work_experience?.company || '',
      },
    };
  };

  const getLiveDetailsHandle = async (slug: string) => {
    try {
      setLoading(true);
      const response = await getLiveDetails(slug);
      const formatted = formatLiveData(response.data);
      setIsRegister(formatted?.isRegister);

      setLiveDetails(formatted);
    } catch (error) {
      console.error('Failed to fetch course details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (slug) {
      getLiveDetailsHandle(slug as string);
    }
  }, [slug, isTokenChanged]);

  // const handleGetBanners = async () => {
  //   try {
  //     const data = await getRegisterStatus({
  //       ecom_user_id: userData?.id as number,
  //       live_workshop_id: liveDetails?.id as number,
  //     });
  //     setIsRegister(!!data?.data?.already_registered);
  //   } catch (e) {
  //     console.warn(e);
  //   }
  // };

  const handleLiveRegistrationAlreadyRegistered = async ({
    phone,
  }: {
    phone?: string;
  }) => {
    try {
      const finalPhone = phone || userData?.phone;

      if (!finalPhone) {
        setPasswordState((prev) => ({
          ...prev,
          modalVisible: true,
        }));
        return;
      }
      setAlreadyRegisterLoading(true);
      await liveRegistrationAlreadyRegistered({
        email: userData?.email as string,
        live_workshop_slug: slug as string,
        name: userData?.name as string,
        ecom_user_id: userData?.id as number,
        phone: finalPhone,
        country_code:
          userData?.user_attributes?.find(
            (item) => item.attribute_key === 'country_code',
          )?.attribute_value || '91',
      });
      const eventParams: EventDetails = {
        event_name: 'workshop_signup',
        source: 'live',
        source_type: 'workshop',
        source_id: workshop_id as string,
        sub_source: null,
        sub_source_id: null,
      };

      if (slug) {
        await getLiveDetailsHandle(slug as string);
      }
      notify('Registered successfully', 'success');
      logEvent({
        eventDetails: eventParams,
        userData,
        pathname: router.pathname,
      });
      if (workshop_id) {
        handleWorkShopSignup(workshop_id as string);
      }
    } catch (e) {
      console.warn(e);
    } finally {
      setAlreadyRegisterLoading(false);
    }
  };

  const setUpdatedProfile = async () => {
    try {
      const { phone, phonePrefix, phoneCountryCode } = passwordState.phoneData;
      const data = await getUserProfile();
      setUserData({
        ...data?.data,
        phone: phone,
        prefix: phonePrefix,
        phoneCountryCode: phoneCountryCode,
      });
      setUserId(data?.data?.id as string);
    } catch (error: any) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    }
  };

  const handlePhoneWithPassword = async () => {
    try {
      setPasswordState((prev) => ({
        ...prev,
        loading: true,
      }));

      const { phone, phonePrefix, phoneCountryCode } = passwordState.phoneData;

      await updateUserPhoneNumber({ phone, country_code: phonePrefix });
      await appwriteService.updateOnboardingPrefs({
        country_code: phonePrefix,
        iso2: phoneCountryCode,
      });
      await setUpdatedProfile();
      await handleLiveRegistrationAlreadyRegistered({
        phone: phone,
      });
      setPasswordState((prev) => ({
        ...prev,
        modalVisible: false,
        loading: false,
        formData: { password: '' },
        errors: {
          ...prev.errors,
          password: '',
        },
      }));
    } catch (error: any) {
      const message = formateErrorObject(error);

      notify(message, 'error');
      setPasswordState((prev) => ({
        ...prev,
        errors: {
          ...prev.errors,
          password: message,
        },
        loading: false,
      }));
    }
  };

  const infoItems = [
    {
      icon: (
        <CalendarFilled className='text-xl md:text-lg lg:text-xl text-[#C7C6CF]' />
      ),
      title: (
        <>
          {day}
          <sup className='text-[10px] md:text-[8px] lg:text-[10px]'>
            {ordinal.toUpperCase()}
          </sup>{' '}
          {monthYear}
        </>
      ),
      subtitle: weekday,
    },
    {
      icon: (
        <ClockCircleFilled className='text-xl md:text-lg lg:text-xl text-[#C7C6CF]' />
      ),
      title: liveDetails?.eventDetails?.time,
      subtitle: liveDetails?.eventDetails?.duration,
    },
    {
      icon: (
        <GlobeIcon className='h-5 w-5 md:h-4 md:w-4 lg:h-5 lg:w-5 text-[#C7C6CF]' />
      ),
      title: 'English',
      subtitle: 'Audio Language',
    },
  ];

  // Events
  useEffect(() => {
    if (!userData) return;
    if (!liveDetails) return;
    EventV2({
      payload: {
        event_name: 'details_view',
        source: 'live',
        source_type: 'workshop',
        source_id: workshop_id as string,
        source_name: liveDetails?.title,
        unit: 'boolean',
        value: 1,
      },
      userData: userData,
      pathname: router.pathname
    })
  }, [userData, liveDetails])

  // details_session event — tracks active seconds, fires automatically when user leaves
  useSessionTracking({
    enabled: !!userData,
    userData,
    payload: {
      event_name: 'details_session',
      source: 'live',
      source_type: 'workshop',
      source_id: workshop_id as string,
      source_name: liveDetails?.title,
    }
  })

  return (
    <>
      <Head>
        <title key='title'>
          {metaData?.title || 'Book Details - ZebraLearn'}
        </title>
        <meta
          name='description'
          content={
            metaData?.description || 'Explore our latest books at ZebraLearn.'
          }
          key='description'
        />
        <meta
          name='keywords'
          content={metaData?.keywords || 'books, learning, education'}
          key='keywords'
        />
        <meta
          name='author'
          content={metaData?.author || 'ZebraLearn'}
          key='author'
        />

        <meta
          property='og:title'
          content={
            metaData?.openGraph?.title || metaData?.title || 'ZebraLearn'
          }
          key='og-title'
        />
        <meta
          property='og:description'
          content={
            metaData?.openGraph?.description || metaData?.description || ''
          }
          key='og-description'
        />
        <meta
          property='og:image'
          content={
            metaData?.openGraph?.image ||
            'https://zebralearn-store.s3.ap-south-1.amazonaws.com/thubnail-creative.png'
          }
          key='og-image'
        />

        <meta
          property='og:image:type'
          content='image/webp'
          key='og-image-type-webp'
        />
        <meta
          property='og:image:type'
          content='image/png'
          key='og-image-type-png'
        />
        {/* Twitter Metadata (Overrides Global) */}
        <meta
          name='twitter:title'
          content={
            metaData?.openGraph?.title || metaData?.title || 'ZebraLearn'
          }
          key='twitter-title'
        />
        <meta
          name='twitter:description'
          content={
            metaData?.openGraph?.description || metaData?.description || ''
          }
          key='twitter-description'
        />
        <meta
          name='twitter:image'
          content={
            metaData?.openGraph?.image ||
            'https://zebralearn-store.s3.ap-south-1.amazonaws.com/OG+1.png?tr=f-png'
          }
          key='twitter-image'
        />
        <meta
          property='article:section'
          content='Website Section'
          key='article-section'
        />
        <meta
          property='article:tag'
          content={metaData?.keywords as string}
          key='article-tag'
        />
      </Head>
      <ShouldRender check={!loading}>
        <div className='flex flex-col mx-auto text-white w-full mt-16'>
          <section className='px-4 md:px-0 py-6 md:py-6 lg:py-16 mx-auto w-full md:w-[90%] lg:w-[88%]'>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className='mx-auto'
            >
              <div className='mb-2 sm:mb-3'>
                <p className='text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl font-semibold leading-snug text-white'>
                  <h1 className='text-2xl md:text-3xl lg:text-[36px] 2xl:text-5xl font-semibold inline'>
                    {liveDetails?.title}
                  </h1>
                  <span className='inline-block  -translate-y-1 2xl:-translate-y-2 border border-white text-white text-xs sm:text-sm md:text-xs lg:text-base px-2 sm:px-3 lg:px-5 py-0.5 ml-2 rounded-full align-middle'>
                    {liveDetails?.tag}
                  </span>
                </p>
              </div>
              {/* <p className='text-textLightGray tracking-tight text-base md:text-lg mb-3 sm:mb-5'>
                {liveDetails?.description}
              </p> */}
              <div className='inline-block px-3 py-1.5 sm:px-4 sm:py-2 border-[#3C3B3E] border rounded-full mb-4 sm:mb-8'>
                <span className='text-sm sm:text-base font-medium'>
                  {liveDetails?.category}
                </span>
              </div>
            </motion.div>

            <div className='flex flex-col-reverse md:flex-row w-full  gap-4 md:gap-x-6 items-stretch lg:mt-8'>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className='bg-[#121212] rounded-[12px] md:w-[50%] xl:w-[35%] md:rounded-[20px] border border-[#3C3B3E] overflow-hidden flex flex-col'
              >
                <div className='grid grid-cols-1 w-full h-fit flex-grow'>
                  {infoItems.map(({ icon, title, subtitle }, index) => (
                    <div
                      key={`${title}-${index}`}
                      className='border-r border-b border-[#3C3B3E] p-4 lg:p-6 flex items-center '
                    >
                      <div className='flex items-center md:items-start gap-x-3 md:gap-y-1 lg:gap-x-3 lg:gap-y-4'>
                        <div className='p-2 lg:p-3 rounded-[8px] bg-[#1C1C1C] flex items-center justify-center'>
                          {icon}
                        </div>
                        <div className='flex flex-col md:mt-2 lg:mt-0'>
                          <p className='font-semibold text-white text-sm md:text-sm lg:text-base'>
                            {title}
                          </p>
                          <p className='text-xs text-[#C7C6CF] md:text-xs lg:text-base'>
                            {subtitle}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className='mt-auto'>
                  <motion.button
                    ref={ref}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={isRegister || alreadyRegisterLoading}
                    onClick={() => {
                      if (userData) {
                        handleLiveRegistrationAlreadyRegistered({});
                      } else {
                        setVisible((prevState) => ({
                          ...prevState,
                          signUp: true,
                        }));
                      }
                    }}
                    className={`${isRegister
                      ? 'bg-[#052E16] text-[#86EFAC]'
                      : 'bg-[#ff5722] hover:bg-[#f04b17] text-white'
                      } font-semibold py-2.5 lg:py-3 transition-colors w-full text-base flex items-center justify-center ${alreadyRegisterLoading
                        ? 'opacity-70 cursor-not-allowed'
                        : ''
                      }`}
                  >
                    {alreadyRegisterLoading && <Spinner />}
                    {isRegister ? 'Seat Reserved Successfully' : 'Enroll Now'}
                  </motion.button>

                  <ShouldRender check={!screens.md}>
                    <AnimatePresence>
                      {showFixedButton && (
                        <motion.div
                          initial={{ y: 100, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{ y: 100, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className='fixed bottom-0 left-0 w-full z-50 px-4 py-4 bg-black border-t border-t-[#1C1C1C]'
                        >
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            disabled={isRegister || alreadyRegisterLoading}
                            onClick={() => {
                              if (userData) {
                                handleLiveRegistrationAlreadyRegistered({});
                              } else {
                                setVisible((prevState) => ({
                                  ...prevState,
                                  signUp: true,
                                }));
                              }
                            }}
                            className={`${isRegister
                              ? 'bg-[#052E16] text-[#86EFAC]'
                              : 'bg-[#ff5722] hover:bg-[#f04b17] text-white'
                              } font-semibold py-2.5 lg:py-3 transition-colors w-full text-base rounded-xl flex items-center justify-center ${alreadyRegisterLoading
                                ? 'opacity-70 cursor-not-allowed'
                                : ''
                              }`}
                          >
                            {alreadyRegisterLoading && <Spinner />}
                            {isRegister
                              ? 'Seat Reserved Successfully'
                              : 'Enroll Now'}
                          </motion.button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </ShouldRender>

                  <ShouldRender
                    check={(liveDetails?.eventDetails.registrations || 0) >= 50}
                  >
                    <p className='text-center font-medium text-sm my-2'>
                      {liveDetails?.eventDetails.registrations}+ Registered
                      Already
                    </p>
                  </ShouldRender>
                </div>
              </motion.div>

              <div className='bg-[#121212] border-[#3C3B3E] md:w-[50%] xl:w-[65%]  p-2 sm:p-3 border rounded-[12px] md:rounded-[20px]   flex flex-col justify-between mt-4 md:mt-0'>
                <div className='z-10 w-full'>
                  <div className='flex justify-center items-center rounded-lg pb-2 sm:p-3 gap-x-2 sm:gap-x-3'>
                    <p className='text-center text-[#C7C6CF] text-xs md:text-sm font-semibold'>
                      Event Starts in:
                    </p>
                    <Countdown
                      startData={liveDetails?.eventDetails?.date || ''}
                      startTime={liveDetails?.eventDetails?.start_time || ''}
                      status={liveDetails?.status || ''}
                      endTime={liveDetails?.eventDetails?.end_time || ''}
                      iframe_type={liveDetails?.iframe_type || ''}
                      iframe={liveDetails?.iframe || ''}
                    />
                  </div>
                </div>
                <div className='w-full aspect-[2/1] max-h-[238px] lg:max-h-[286px] xl:max-h-[346px] overflow-hidden rounded-[4px] md:rounded-[12px] border border-[#3C3B3E] relative bg-black'>
                  <Image
                    src={liveDetails?.thumbnail as string}
                    alt='Event Thumbnail'
                    fill
                    className='object-contain'
                    sizes='(max-width: 768px) 100vw, 50vw'
                  />
                </div>
              </div>
            </div>
            <ShouldRender check={isRegister}>
              <div className='flex items-center justify-between mb-4 py-4 gap-x-4 px-4 lg:px-7 rounded-[16px] mt-12 bg-[#151515]'>
                <div className='flex items-center space-x-2 lg:space-x-3'>
                  <div className='flex flex-col gap-y-1'>
                    <h3 className='font-semibold text-white text-xs lg:text-xl'>
                      Invite a Friend and Get a Gift
                    </h3>
                    <p className='font-[400] text-textGrayColor text-[10px] lg:text-base '>
                      When you invite a friend to workshop you will be rewarded
                      a Gift if they register.
                    </p>
                  </div>
                </div>
                <ShouldRender check={screens.lg}>
                  <Button
                    size='large'
                    type='primary'
                    onClick={() => {
                      if (userData?.zl_uid) {
                        router.push('/refer-earn');
                      } else {
                        setVisible((prevState) => ({
                          ...prevState,
                          login: true,
                        }));
                        notify('Please login to invite a friend', 'warning');
                      }
                    }}
                    className='px-5 py-3'
                  >
                    Invite a Friend <ChevronRightIcon className='w-5 h-5' />
                  </Button>
                </ShouldRender>
                <ShouldRender check={!screens.lg}>
                  <div
                    onClick={() => {
                      if (userData?.zl_uid) {
                        router.push('/refer-earn');
                      } else {
                        setVisible((prevState) => ({
                          ...prevState,
                          login: true,
                        }));
                        notify('Please login to invite a friend', 'warning');
                      }
                    }}
                    className='bg-[#282829] text-white  font-semibold  rounded-full w-6 h-6 flex justify-center items-center  text-sm gap-1'
                  >
                    <ChevronRightIcon className='w-5 h-5s' />
                  </div>
                </ShouldRender>
              </div>
            </ShouldRender>
          </section>

          <section className='bg-[#121212] px-4 sm:px-6 md:px-8 lg:px-[6%] py-6 sm:py-6 md:py-8 lg:py-12'>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className='text-center mb-6 sm:mb-6 md:mb-8 lg:mb-10 px-auto w-full md:w-11/12 mx-auto'
            >
              <h2 className='text-2xl md:text-2xl lg:text-[28px] font-semibold mb-2 sm:mb-3 md:mb-3'>
                Why Choose This Live Class?
              </h2>
            </motion.div>

            <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-3 lg:gap-4'>
              {liveDetails?.benefits.map((benefit, index) => (
                <motion.div
                  key={`${benefit.title}-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 * (index + 1) }}
                  className='bg-[#1C1C1C] border border-[#FFFFFF0D] md:h-[90px] lg:h-[100px] justify-start md:justify-center rounded-[12px] md:rounded-[12px] lg:rounded-[16px] p-3 md:p-4 lg:p-5 flex items-center gap-3 sm:gap-3'
                >
                  <div className='flex-shrink-0 text-lg md:text-base lg:text-lg'>
                    {benefit.icon}
                  </div>
                  <p className='font-medium text-sm md:text-xs lg:text-sm'>
                    {benefit.title}
                  </p>
                </motion.div>
              ))}
            </div>
          </section>

          <section className=' md:mx-auto md:w-[90%] lg:w-[88%] px-4 md:px-0 py-8 md:py-10 lg:py-12'>
            <div className='flex flex-col md:flex-row lg:flex-row gap-y-8 md:gap-x-[60px] lg:gap-x-[100px]'>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className='w-full md:w-6/12 lg:w-5/12'
              >
                <h2 className='text-2xl md:text-2xl lg:text-[28px] font-semibold mb-2 md:mb-2 lg:mb-3'>
                  What You'll Learn
                </h2>
                <p className='text-textLightGray text-sm md:text-sm lg:text-base mb-4 md:mb-6 lg:mb-8'>
                  {liveDetails?.description}
                </p>
                <ShouldRender check={!isRegister}>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      if (userData) {
                        handleLiveRegistrationAlreadyRegistered({});
                      } else {
                        setVisible((prevState) => ({
                          ...prevState,
                          signUp: true,
                        }));
                      }
                    }}
                    className='bg-[#ff5722] hover:bg-[#f04b17] w-full justify-center text-white font-semibold text-base py-3 px-6 rounded-lg transition-colors flex items-center gap-2'
                  >
                    Enroll Now
                    <ChevronRightIcon className='h-6 w-6' />
                  </motion.button>
                </ShouldRender>
              </motion.div>

              <span className='rounded-[12px] md:rounded-[16px] border border-[#282829] h-fit w-full md:w-6/12 lg:w-7/12'>
                <Collapse
                  expandIconPosition='end'
                  className='border'
                  expandIcon={(props) => customExpandIcon(props)}
                  accordion
                >
                  {liveDetails?.modules?.map((section, index) => (
                    <Panel
                      className='customBorder customCollapseBg bg-readerLightGrayBg rounded-t-[12px] md:rounded-t-[16px] border-[#282829]'
                      header={
                        <div className='flex flex-col px-2 lg:px-4 py-1 lg:py-2 gap-1'>
                          <span
                            style={{
                              lineHeight: '20px',
                            }}
                            className='font-medium text-xs text-[#9F9EA6]'
                          >
                            MODULE {index + 1}
                          </span>
                          <span className='text-base font-semibold text-white'>
                            {' '}
                            {' ' + section.title}
                          </span>
                        </div>
                      }
                      key={`${section.content}-${index}`}
                    >
                      <div className='list-disc'>{section.content}</div>
                    </Panel>
                  ))}
                </Collapse>
              </span>
            </div>
          </section>

          <section className='container mx-auto md:px-4 py-10 md:py-16'>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className='w-full md:w-[90%] lg:w-[85%] mx-auto'
            >
              <h2 className='text-2xl md:text-2xl lg:text-3xl font-bold mb-6 md:mb-6 lg:mb-8 text-center'>
                Meet the Author
              </h2>

              <LiveAuthorCard
                author={liveDetails?.author as Author}
                productId={workshop_id as string}
                productName={liveDetails?.title as string}
              />
              <div className='mt-8 flex justify-center'>
                <ShouldRender check={!isRegister}>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      if (userData) {
                        handleLiveRegistrationAlreadyRegistered({});
                      } else {
                        setVisible((prevState) => ({
                          ...prevState,
                          signUp: true,
                        }));
                      }
                    }}
                    className='bg-[#ff5722] hover:bg-[#f04b17] text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center gap-2'
                  >
                    Enroll Now
                    <ChevronRightIcon className='h-5 w-5' />
                  </motion.button>
                </ShouldRender>
              </div>
            </motion.div>
          </section>
          <section className='px-4 py-6 lg:py-10 mx-auto w-full sm:w-[80%] md:w-[75%] lg:w-[65%] xl:w-[40%]'>
            <motion.div
              className='w-full'
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <motion.h2
                className='text-xl md:text-2xl lg:text-[32px] text-center py-3 sm:py-3 md:py-8 lg:py-10 font-semibold text-white'
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                Frequently Asked Questions
              </motion.h2>

              <div className='space-y-2 sm:space-y-3 md:space-y-4'>
                {faqItems.map((item, index) => (
                  <motion.div
                    key={`${item.answer}-${index}`}
                    className={clsx(
                      activeIndex === index ? 'bg-[#282829]' : 'bg-[#1C1C1C]',
                      'rounded-[8px] sm:rounded-[10px] overflow-hidden',
                    )}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 + 0.2 }}
                  >
                    <motion.button
                      className='w-full flex items-center justify-between px-3 sm:px-4 md:px-4 lg:px-5 py-3 sm:py-3 md:py-3 lg:py-4 text-left text-white'
                      onClick={() => toggleAccordion(index)}
                      whileHover={{
                        backgroundColor: 'rgba(40, 40, 41, 0.5)',
                      }}
                    >
                      <span className='text-sm md:text-base font-medium'>
                        {item.question}
                      </span>
                      <motion.div
                        animate={{
                          rotate: activeIndex === index ? 45 : 0,
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        <PlusIcon className='h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 text-[#FFFFFF99]' />
                      </motion.div>
                    </motion.button>

                    <AnimatePresence>
                      {activeIndex === index && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{
                            duration: 0.3,
                            ease: 'easeInOut',
                          }}
                          className='overflow-hidden'
                        >
                          <div className='px-4 sm:px-4 md:px-4 lg:px-5 py-3 sm:py-3 md:py-3 lg:py-4 pt-0 text-sm md:text-xs lg:text-sm text-[#C7C6CF]'>
                            {item.answer}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </section>
          <section className='px-4 md:px-0 py-6 md:py-6 lg:py-16 mx-auto w-full md:w-[90%] lg:w-[88%]'>
            <RecommendedBooksSection
              category={liveDetails?.category ?? undefined}
              token={userData?.zl_uid as string}
            />
          </section>
        </div>
      </ShouldRender>
      <ShouldRender check={loading}>
        <CustomLoader />
      </ShouldRender>

      <SignUpModal
        onCancel={onCancelCloseModal}
        visible={visible.signUp}
        isRegister={true}
        noRedirect={true}
        liveSlug={slug as string}
      />
      <LoginModal onCancel={onCancelCloseLoginModal} visible={visible.login} />
      <ForgotPasswordModal
        onCancel={onCancelCloseForgotPasswordModal}
        visible={visible.forgotPassword}
      />
      <PhonePasswordLiveModal
        visible={passwordState.modalVisible}
        onCancel={() =>
          setPasswordState((prev) => ({ ...prev, modalVisible: false }))
        }
        errorsPassword={{ password: passwordState.errors.password }}
        errorsPhone={{ phone: passwordState.errors.phone }}
        formDataPassword={passwordState.formData}
        formDataPhone={passwordState.phoneData}
        setFormDataPassword={setFormDataPassword}
        setFormDataPhone={setFormDataPhone}
        handlePhoneInput={handlePhoneInput}
        handlePhoneWithPassword={handlePhoneWithPassword}
        loading={passwordState.loading}
      />
    </>
  );
};

export default LiveLandingPage;
